import * as Sentry from '@sentry/react';
import { Severity } from '@sentry/react';

export interface LogContext {
  route?: string;
  userRole?: string | null;
  userId?: string | null;
  httpStatus?: number;
  responseBodySize?: number;
  tokenExpiry?: string | null;
  errorType?: string;
  componentStack?: string;
}

/**
 * Logger utility for diagnostic logging
 * Logs to console in development and to Sentry in production
 */
class Logger {
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
  }

  /**
   * Log informational messages
   */
  info(message: string, context?: LogContext): void {
    const logData = this.formatLog('INFO', message, context);
    console.info(logData);

    if (!this.isDevelopment) {
      Sentry.addBreadcrumb({
        message,
        level: Severity.Info,
        data: context,
      });
    }
  }

  /**
   * Log warnings
   */
  warn(message: string, context?: LogContext): void {
    const logData = this.formatLog('WARN', message, context);
    console.warn(logData);

    if (!this.isDevelopment) {
      Sentry.addBreadcrumb({
        message,
        level: Severity.Warning,
        data: context,
      });
    }
  }

  /**
   * Log errors
   */
  error(message: string, error?: Error | any, context?: LogContext): void {
    const errorType = error instanceof Error ? error.message : typeof error === 'string' ? error : undefined;
    const logData = this.formatLog('ERROR', message, { ...context, errorType });
    console.error(logData, error);

    if (!this.isDevelopment) {
      Sentry.captureException(error || new Error(message), {
        contexts: {
          diagnostic: context ? { ...context } : {},
        },
        tags: {
          userRole: context?.userRole || 'unknown',
          route: context?.route || 'unknown',
        },
      });
    }
  }

  /**
   * Log route changes
   */
  logRouteChange(from: string, to: string, context?: LogContext): void {
    this.info(`Route change: ${from} -> ${to}`, {
      ...context,
      route: to,
    });
  }

  /**
   * Log authentication state changes
   */
  logAuthStateChange(state: string, context?: LogContext): void {
    this.info(`Auth state change: ${state}`, context);
  }

  /**
   * Log API failures
   */
  logApiFailure(endpoint: string, status: number, context?: LogContext): void {
    this.error(`API failure: ${endpoint}`, undefined, {
      ...context,
      httpStatus: status,
    });
  }

  /**
   * Get token expiry from JWT
   */
  getTokenExpiry(token: string | null): string | null {
    if (!token) return null;

    try {
      // Decode JWT without verification (just reading the payload)
      const base64Url = token.split('.')[1];
      if (!base64Url) return null;

      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join('')
      );

      const payload = JSON.parse(jsonPayload);
      if (payload.exp) {
        const expiryDate = new Date(payload.exp * 1000);
        const now = new Date();
        const timeUntilExpiry = expiryDate.getTime() - now.getTime();
        const minutesUntilExpiry = Math.floor(timeUntilExpiry / 1000 / 60);

        return `${expiryDate.toISOString()} (${minutesUntilExpiry} minutes)`;
      }
    } catch (error) {
      console.warn('Failed to decode token expiry:', error);
    }

    return null;
  }

  /**
   * Format log data for consistent output
   */
  private formatLog(level: string, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` | Context: ${JSON.stringify(context)}` : '';
    return `[${timestamp}] [${level}] ${message}${contextStr}`;
  }
}

// Export singleton instance
export const logger = new Logger();
